import React from "react";
import { useQuery, QueryObserverOptions } from "react-query";
import { useI18n } from "@haohan/root-locales";
import { SiteQueryContext } from "./QueryConfig";
import { UseSsrQueryLoadData } from "./types";


function getFullKey<P>(key: string, params: P) {
	const urlParams = new URLSearchParams();
	const paramPairs = Object.entries(params).sort((a, b) => a[0] > b[0] ? 1 : -1);
	for (const [k, v] of paramPairs) {
		urlParams.set(k, v.toString());
	}
	return `${key}?${urlParams.toString()}`;
}

type SsrQueryOptions<R> = QueryObserverOptions<R> & {throttlingDelay?: number};

export function useSsrQuery<P, R>(
	baseKey: string,
	loadData: UseSsrQueryLoadData<P, R>,
	params?: P,
	options?: SsrQueryOptions<R>
) {
	const { axios, queries, ssr } = React.useContext(SiteQueryContext);
	const { lang } = useI18n();
	const fullKey = getFullKey(baseKey, {...params, __currentLang: lang});
	const load = async () => {
		const [r] = await Promise.all([
			loadData(axios, {key: fullKey, params}),
			new Promise(
				resolve => setTimeout(
					resolve, (!ssr && options?.throttlingDelay) || 0)
				)
		]);
		return r;
	};
	if (ssr && queries) {
		queries.push({baseKey, fullKey, load})
	}
	const queryData = useQuery<R>(fullKey, load, options);
	if (ssr) {
		queryData.isLoading = false;
		queryData.isFetching = false;
	}
	return queryData;
}
